import { useEffect, useState } from "react";
import { PageHeader } from "../components/layout";
import { ApiUnavailableState, EmptyState, LoadingState, NotConfiguredState } from "../components/states";
import { Card, StatusIndicator, type StatusTone } from "../components/ui";
import { useSession } from "../hooks/useSession";
import {
  api,
  apiConfigured,
  ApiError,
  type ForgeFindingRecord,
  type ForgeRunResult,
  type LaunchReadinessGate,
  type LaunchReadinessView,
  type SafeObservabilityView,
  type SecurityRegressionSummary,
  type SloStatus,
} from "../lib/api";

/**
 * Phase 10B — Launch readiness (platform STAFF view).
 *
 * SAFETY: this is a READ-ONLY staff surface. It shows the launch-readiness gates,
 * the latest Forge QA run summary + findings (advisory AI findings are labelled as
 * such), the cross-phase security-regression status, and the SAFE platform
 * observability posture + SLOs. It exposes NO tenant data or secrets, and there is
 * deliberately NO "launch" or "approve" button — a Forge run is mock + synthetic +
 * locks-engaged and is never a launch authorization, and launch sign-off is
 * human-only and happens out-of-band.
 */

function toMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  return "Something went wrong. Please try again.";
}

function isPlatformStaff(platformRole: string): boolean {
  return platformRole === "ADMIN" || platformRole === "OWNER";
}

export interface LaunchReadinessBundle {
  readiness: LaunchReadinessView;
  regression: SecurityRegressionSummary;
  observability: SafeObservabilityView;
  /** The most-recent Forge run + its findings, or null if none has run yet. */
  latestRun: ForgeRunResult | null;
}

export function LaunchReadiness(): JSX.Element {
  const { state } = useSession();

  if (!apiConfigured) {
    return (
      <>
        <PageHeader title="Launch readiness (staff)">
          <p>The platform launch-readiness gates, latest QA run, security regression and safe observability posture.</p>
        </PageHeader>
        <NotConfiguredState title="Control API URL not configured">
          Set <code>VITE_CONTROL_API_URL</code> to read launch readiness.
        </NotConfiguredState>
      </>
    );
  }

  return (
    <>
      <PageHeader title="Launch readiness (staff)">
        <p>
          A read-only platform-staff view of the launch-readiness gates, the latest ODIN Forge QA run, the cross-phase
          security regression and the safe platform observability posture. Sign-off is human-only and out-of-band — there
          is no launch or approve action here.
        </p>
      </PageHeader>
      {state.kind === "loading" ? (
        <LoadingState label="Checking your session…" />
      ) : state.kind !== "authenticated" ? (
        <EmptyState title="Sign in required">This staff view requires an authenticated platform operator.</EmptyState>
      ) : !isPlatformStaff(state.me.platformRole) ? (
        <EmptyState title="Platform staff only">
          Launch readiness is a platform-administrator surface. Your account is not a platform operator.
        </EmptyState>
      ) : (
        <ReadinessLoader />
      )}
    </>
  );
}

function ReadinessLoader(): JSX.Element {
  const [bundle, setBundle] = useState<LaunchReadinessBundle | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setBundle(null);
    setError(null);
    (async () => {
      try {
        const [readiness, regression, observability, runs] = await Promise.all([
          api.forge.launchReadiness(),
          api.forge.securityRegression(),
          api.observability(),
          api.forge.listRuns(),
        ]);
        const mostRecent = runs.runs[0];
        const latestRun = mostRecent ? await api.forge.getRun(mostRecent.id) : null;
        if (!cancelled) setBundle({ readiness, regression, observability, latestRun });
      } catch (e) {
        if (!cancelled) setError(toMessage(e));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (error) return <ApiUnavailableState detail={error} />;
  if (!bundle) return <LoadingState label="Reading launch readiness…" />;
  return <LaunchReadinessBoard bundle={bundle} />;
}

/** Presentational board — renders entirely from the provided (already-safe) data. */
export function LaunchReadinessBoard({ bundle }: { bundle: LaunchReadinessBundle }): JSX.Element {
  const { readiness, regression, observability, latestRun } = bundle;
  return (
    <div className="ob-builder">
      <div className="dryrun-banner" role="note">
        <strong>Read-only staff view.</strong>
        <span>
          A green checklist is evidence, not an authorization. Launch sign-off and production activation are human-only —
          there is no launch or approve action on this page.
        </span>
      </div>

      <ReadinessGates readiness={readiness} />
      <RegressionCard regression={regression} />
      <LatestRunCard latestRun={latestRun} />
      <ObservabilityCard observability={observability} />
    </div>
  );
}

const GATE_TONE: Record<LaunchReadinessGate["status"], StatusTone> = {
  pass: "ok",
  outstanding: "warn",
};

function ReadinessGates({ readiness }: { readiness: LaunchReadinessView }): JSX.Element {
  return (
    <Card
      title="Launch-readiness gates"
      description={`Environment: ${readiness.environment}. ${readiness.note}`}
    >
      <ul className="chip-list" aria-label="Gate summary" style={{ marginBottom: "0.75rem" }}>
        <li className={`count-chip ${readiness.allGatesPass ? "count-chip--ok" : ""}`}>
          {readiness.gates.filter((g) => g.status === "pass").length}/{readiness.gates.length} gates pass
        </li>
        <li className="count-chip">sign-off: human-only</li>
      </ul>
      <div className="table" role="table" aria-label="Launch-readiness gates">
        <div className="table__head" role="row" style={{ gridTemplateColumns: "1.4fr 120px 2fr" }}>
          <span role="columnheader">Gate</span>
          <span role="columnheader">Status</span>
          <span role="columnheader">Detail</span>
        </div>
        {readiness.gates.map((g) => (
          <div className="table__row" role="row" key={g.id} style={{ gridTemplateColumns: "1.4fr 120px 2fr" }}>
            <span role="cell">{g.title}</span>
            <span role="cell">
              <StatusIndicator tone={GATE_TONE[g.status]} label={g.status === "pass" ? "pass" : "outstanding"} />
            </span>
            <span role="cell" className="muted">{g.detail}</span>
          </div>
        ))}
      </div>
    </Card>
  );
}

function RegressionCard({ regression }: { regression: SecurityRegressionSummary }): JSX.Element {
  const total = regression.invariants.length;
  const failed = regression.latestChecks.filter((c) => c.outcome === "FAIL").length;
  const passed = total - failed;
  return (
    <Card
      title="Cross-phase security regression"
      description="The standing safety-invariant catalogue and the latest run's gate result. Assert the full gate with pnpm security:regression."
    >
      <ul className="chip-list" aria-label="Security-regression summary">
        <li className={`count-chip ${regression.regressionPassed ? "count-chip--ok" : ""}`}>
          {regression.regressionPassed ? "regression passing" : "regression not green"}
        </li>
        <li className="count-chip">{passed}/{total} invariants pass</li>
        {regression.latestRun ? (
          <li className="count-chip">latest run: {regression.latestRun.state}</li>
        ) : (
          <li className="count-chip">no run recorded yet</li>
        )}
      </ul>
    </Card>
  );
}

const SEVERITY_TONE: Record<string, StatusTone> = {
  critical: "danger",
  high: "danger",
  medium: "warn",
  low: "neutral",
  info: "neutral",
};

function LatestRunCard({ latestRun }: { latestRun: ForgeRunResult | null }): JSX.Element {
  if (!latestRun) {
    return (
      <Card title="Latest Forge QA run">
        <EmptyState title="No Forge QA run recorded">
          No ODIN Forge run exists yet. A run is a staging-only, mock + synthetic, locks-engaged QA exercise — start one
          from the platform tooling. It is never a launch authorization.
        </EmptyState>
      </Card>
    );
  }
  const { run, summary, findings } = latestRun;
  return (
    <Card
      title="Latest Forge QA run"
      description="Mock adapters + synthetic data with production locks engaged — evidence for staging acceptance, never a launch authorization."
    >
      <ul className="chip-list" aria-label="Forge run summary" style={{ marginBottom: "0.75rem" }}>
        <li className={`count-chip ${run.state === "PASSED" ? "count-chip--ok" : ""}`}>run: {run.state}</li>
        <li className="count-chip">{summary.scenariosPassed}/{summary.scenariosRun} scenarios pass</li>
        <li className="count-chip">{summary.invariantFailures} invariant failures</li>
        <li className="count-chip">{summary.findings} findings</li>
      </ul>
      {findings.length === 0 ? (
        <p className="muted">No findings recorded for this run.</p>
      ) : (
        <FindingsTable findings={findings} />
      )}
    </Card>
  );
}

function FindingsTable({ findings }: { findings: ForgeFindingRecord[] }): JSX.Element {
  const COLS = "110px 1fr 130px 1.6fr";
  return (
    <div className="table" role="table" aria-label="Forge run findings">
      <div className="table__head" role="row" style={{ gridTemplateColumns: COLS }}>
        <span role="columnheader">Severity</span>
        <span role="columnheader">Finding</span>
        <span role="columnheader">Source</span>
        <span role="columnheader">Detail</span>
      </div>
      {findings.map((f) => (
        <div className="table__row" role="row" key={f.id} style={{ gridTemplateColumns: COLS }}>
          <span role="cell">
            <StatusIndicator tone={SEVERITY_TONE[f.severity] ?? "neutral"} label={f.severity} />
          </span>
          <span role="cell">{f.title}</span>
          <span role="cell">
            {f.source === "ai-critic" ? (
              <span className="count-chip count-chip--warn" title="AI-critic findings are advisory only and require human triage.">
                AI · advisory
              </span>
            ) : (
              <span className="count-chip">deterministic</span>
            )}
          </span>
          <span role="cell" className="muted">{f.detail}</span>
        </div>
      ))}
    </div>
  );
}

const POSTURE_TONE: Record<string, StatusTone> = {
  operational: "ok",
  maintenance: "neutral",
  degraded: "warn",
  partial_outage: "warn",
  major_outage: "danger",
  unknown: "neutral",
};

function ObservabilityCard({ observability }: { observability: SafeObservabilityView }): JSX.Element {
  return (
    <Card
      title="Platform observability (safe posture)"
      description="Coarse, non-identifying signals only — component states, allowlisted metrics and SLOs. No tenant data or secrets."
    >
      <div className="row" style={{ alignItems: "center", gap: "0.75rem", marginBottom: "0.75rem" }}>
        <StatusIndicator tone={POSTURE_TONE[observability.posture] ?? "neutral"} label={`posture: ${observability.posture}`} />
        {observability.version ? <span className="muted">version {observability.version}</span> : null}
      </div>

      <ul className="chip-list" aria-label="Component states" style={{ marginBottom: "0.75rem" }}>
        {observability.components.map((c) => (
          <li key={c.component} className="count-chip">
            {c.component}: {c.state}
          </li>
        ))}
      </ul>

      {observability.slos.length > 0 ? <SloTable slos={observability.slos} /> : null}

      {observability.metrics.length > 0 ? (
        <ul className="chip-list" aria-label="Allowlisted metrics" style={{ marginTop: "0.75rem" }}>
          {observability.metrics.map((m, i) => (
            <li key={`${m.name}-${i}`} className="count-chip">
              {m.name}: {m.value}
            </li>
          ))}
        </ul>
      ) : (
        <p className="muted" style={{ marginTop: "0.5rem" }}>
          No allowlisted metric samples are populated yet.
        </p>
      )}
    </Card>
  );
}

function SloTable({ slos }: { slos: SloStatus[] }): JSX.Element {
  const COLS = "1.4fr 110px 130px 130px";
  return (
    <div className="table" role="table" aria-label="Service-level objectives">
      <div className="table__head" role="row" style={{ gridTemplateColumns: COLS }}>
        <span role="columnheader">SLO</span>
        <span role="columnheader">Objective</span>
        <span role="columnheader">Success</span>
        <span role="columnheader">Error budget</span>
      </div>
      {slos.map((s) => (
        <div className="table__row" role="row" key={s.name} style={{ gridTemplateColumns: COLS }}>
          <span role="cell">
            {s.name} {s.breached ? <span className="count-chip count-chip--warn">breached</span> : null}
          </span>
          <span role="cell">{(s.objective * 100).toFixed(2)}%</span>
          <span role="cell">{(s.successRatio * 100).toFixed(2)}%</span>
          <span role="cell">{(s.errorBudgetRemaining * 100).toFixed(0)}%</span>
        </div>
      ))}
    </div>
  );
}
